import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ProductService from '../../api/ProductService';
import { BeschreibungTab } from '../../components/AddProductModalTabs/BeschreibungTab';
import { DokumenteTab } from '../../components/AddProductModalTabs/DokumenteTab';
import { KundenTab } from '../../components/AddProductModalTabs/KundenTab';
import { ZubehorTab } from '../../components/AddProductModalTabs/ZubehorTab';
import { CustomButton } from '../../components/CustomButton/CustomButton';
import { Info } from '../../components/Info/Info';
import { TextField } from '../../components/TextField/TextField';
import { useSaveProduct } from '../../hooks/useSaveProduct';
import { productManagementActions } from '../../store/product-management-slice';
import { SalesUnit } from '../../types/product';
import { allowOnlyDecimals } from '../../utils/allowOnlyDecimals';

import styles from './addProductModal.module.css';
interface AddProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onProductAdded?: () => void;
}

const tabs = [
    {
        id: 1,
        name: 'Beschreibung'
    },
    {
        id: 2,
        name: 'Zubehör'
    },
    {
        id: 3,
        name: 'Kundenpreis'
    },
    {
        id: 4,
        name: 'Dokumente'
    }
]
export const AddProductModal = ({ isOpen, onClose, onProductAdded }: AddProductModalProps) => {
    const dispatch = useDispatch();
    const product = useSelector((state: any) => state.productManagement.product);
    const { saveProduct, updateProduct, isSaving, error, setError } = useSaveProduct();

    const [showDropdown, setShowDropdown] = useState(false);
    const [unitsDropdown, setUnitsDropdown] = useState<SalesUnit[]>([]);
    const [selectedTab, setSelectedTab] = useState<number>(1);

    // SevDesk owns the article data of a SevDesk article; only Zubehör and Dokumente are
    // edited here.
    const isFromSevDesk = Boolean(product.isFromSevDesk);
    const visibleTabs = isFromSevDesk ? tabs.filter((tab) => tab.name !== 'Kundenpreis') : tabs;

    const handleDropdownClick = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isFromSevDesk) return;
        setShowDropdown(!showDropdown);
    };

    const handleOptionClick = (option: SalesUnit) => {
        dispatch(productManagementActions.setSalesUnitId(option.id));
        setShowDropdown(false);
    };

    const getProductUnits = async () => {
        try {
            const response = await ProductService.GetProductUnits();
            setUnitsDropdown(response.data);
        } catch (error) {
            console.error(error);
        }
    }


    const resetAndClose = () => {
        onClose();
        setSelectedTab(1);
        dispatch(productManagementActions.resetState());
        setError("");
    }

    const closeModal = () => {
        if (isSaving) return;

        resetAndClose();
    }

    const handleName = (name: string) => {
        dispatch(productManagementActions.setProductName(name));
    };
    const handleReferenceNumber = (number: string) => {
        dispatch(productManagementActions.setReferenceNumber(number));
    };
    const handlePurchasePrice = (value: string) => {
        dispatch(productManagementActions.setPurchasePrice(value));
    }

    const handlePrice = (value: string) => {
        dispatch(productManagementActions.setPrice(value));
    }

    // On failure the modal stays open with the error, so nothing that was entered is lost.
    const onSaveProduct = async () => {
        try {
            if (product.id)
                await updateProduct();
            else
                await saveProduct();
        } catch {
            return;
        }

        onProductAdded?.();
        resetAndClose();
    }

    const selectedOption = useMemo(() => unitsDropdown.find((option) => option.id === product.salesUnitId)?.name, [unitsDropdown, product.salesUnitId]);

    useEffect(() => {
        getProductUnits();
    }, []);

    if (!isOpen) return null;
    return (
        <div className={styles["modal-overlay"]} onClick={closeModal}>
            <div
                className={styles["modal-content"]}
                onClick={(e) => e.stopPropagation()}
            >
                {error && <Info type="error">{error}</Info>}
                <p className={styles["modal-title"]}>{product.name}</p>
                {isFromSevDesk && (
                    <p className={styles["modal-subtitle"]}>Artikeldaten werden in SevDesk gepflegt. Hier können Zubehör und Dokumente bearbeitet werden.</p>
                )}
                <button className={styles["close-button"]} onClick={closeModal}>
                    &times;
                </button>

                <div className={styles["modal-body"]}>

                    <div>
                        <TextField
                            label="Produktname"
                            disabled={isFromSevDesk}
                            value={product.name}
                            onChange={(e) => handleName(e.target.value)}
                            customStyles={{ marginBottom: "16px" }}
                        />
                        <TextField
                            label="Art.-Nr."
                            disabled={isFromSevDesk}
                            value={product.referenceNumber}
                            onChange={(e) => handleReferenceNumber(e.target.value)}
                            customStyles={{ marginBottom: "16px" }}

                        />
                        <TextField
                            label="Einkaufspreis (Netto)"
                            disabled={isFromSevDesk}
                            value={product.price?.purchasePrice}
                            placeholder="0,00"
                            onChange={(e) => {
                                const input = allowOnlyDecimals(e.target.value);
                                handlePurchasePrice(input)
                            }}
                            inputAlign='right'
                            type='text'
                            inputMode="decimal"
                            suffix="EUR"
                            mutedPlaceholder
                        />
                    </div>
                    <div>
                        <div className={styles["dropdown-container"]}>
                            <label htmlFor="dropdown">Standardeinheit</label>
                            <div className={styles["dropdown"] + (isFromSevDesk ? ' ' + styles["dropdown--disabled"] : '')} onClick={handleDropdownClick}>
                                <input
                                    id="dropdown"
                                    type="text"
                                    value={selectedOption || ''}
                                    disabled={isFromSevDesk}
                                    readOnly
                                />
                                <KeyboardArrowDownIcon className={styles["dropdown-icon-svg"]} />
                                {showDropdown && (
                                    <div className={styles["dropdown-list"]}>
                                        {unitsDropdown.map((option) => (
                                            <div
                                                key={option.id}
                                                className={styles["dropdown-option"]}
                                                onClick={() => handleOptionClick(option)}
                                            >
                                                {option.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <TextField
                            label="Umsatzsteuer in %"
                            disabled={true}
                            value="19%"
                            onChange={() => null}
                            customStyles={{ marginTop: "16px" }}
                        />
                        <TextField
                            label="Verkaufspreis (Netto)"
                            disabled={isFromSevDesk}
                            value={product.price?.price}
                            placeholder="0,00"
                            onChange={(e) => {
                                const input = allowOnlyDecimals(e.target.value);
                                handlePrice(input);
                            }}
                            customStyles={{ marginTop: "16px" }}
                            inputAlign='right'
                            type='text'
                            inputMode="decimal"
                            suffix="EUR"
                            mutedPlaceholder
                        />
                    </div>
                </div>

                <div className={styles["modal-submenu"]}>
                    <div className={styles["modal-submenu-tabs"]}>
                        {
                            visibleTabs.map((tab) => (
                                <div
                                    key={tab.id}
                                    className={styles["modal-submenu-tab"] + (selectedTab === tab.id ? ' ' + styles["modal-submenu-tab-selected"] : '')}
                                    onClick={() => setSelectedTab(tab.id)}
                                >
                                    {tab.name}
                                </div>
                            ))
                        }
                    </div>
                    {selectedTab === 1 && <BeschreibungTab />}
                    {selectedTab === 2 && <ZubehorTab />}
                    {selectedTab === 3 && <KundenTab />}
                    {selectedTab === 4 && <DokumenteTab />}
                </div>
                <div className={styles["modal-footer"]}>
                    <CustomButton disabled={isSaving} color='outlined' customClass={styles["modal-button"]} onClick={closeModal}>Abbrechen</CustomButton>
                    <CustomButton loading={isSaving} disabled={(!isFromSevDesk && (!product.name || !product.referenceNumber)) || isSaving} color='desktopGreen' customClass={styles["modal-button"]} onClick={onSaveProduct}>Speichern</CustomButton>
                </div>
            </div>
        </div>
    )
};
